import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { userList } from "../../api/Links";
import { useTranslation } from "react-i18next";
import CustomPageHeader from "../../components/pages/CustomPageHeader";
import Loader from "../../components/Loader";
import { useTheme } from "../../context/ThemeContext";
import { toast } from "react-toastify";

const AddIndividual = ({ onBack }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(
    location.state?.members || []
  );
  const selectedGroups = location.state?.selectedGroups || [];
  const formData = location.state?.formData || {};
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestInProgress = useRef(false);
  const observerRef = useRef(null);
  const loaderRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const stateRef = useRef({ loading: false, page: 1, lastPage: null });
  const pagingGateRef = useRef(false);
  useEffect(() => {
    if (requestInProgress.current) return;

    requestInProgress.current = true;
    setLoading(true);
    setError("");

    userList(page, debouncedSearch)
      .then((res) => {
        const newUsers = Array.isArray(res?.users) ? res.users : [];
        const apiLastPage = Number(res?.lastPage || 1);

        setUsers((prev) => {
          if (page === 1) return newUsers;
          const existingIds = new Set(prev.map((u) => u.id));
          const filtered = newUsers.filter((u) => !existingIds.has(u.id));
          return [...prev, ...filtered];
        });

        setLastPage(apiLastPage);
      })
      .catch((err) => setError(err.message || "Failed to fetch users"))
      .finally(() => {
        setLoading(false);
        requestInProgress.current = false;
        pagingGateRef.current = false;
      });
  }, [page, debouncedSearch]);

  // Debounce search input to limit API calls
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  // Reset list and pagination when search changes
  useEffect(() => {
    setUsers([]);
    setPage(1);
    setLastPage(null);
  }, [debouncedSearch]);

  // Keep a mirror of state for observer callback
  useEffect(() => {
    stateRef.current.loading = loading;
  }, [loading]);
  useEffect(() => {
    stateRef.current.page = page;
  }, [page]);
  useEffect(() => {
    stateRef.current.lastPage = lastPage;
  }, [lastPage]);

  // ✅ Infinite scroll observer (rooted to the scroll container)
  useEffect(() => {
    const loaderEl = loaderRef.current;
    const rootEl = scrollContainerRef.current;
    if (!loaderEl || !rootEl) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const { loading, page, lastPage } = stateRef.current;
        const noMorePages = lastPage !== null && page >= lastPage;

        if (
          entry.isIntersecting &&
          !loading &&
          !requestInProgress.current &&
          !pagingGateRef.current &&
          !noMorePages
        ) {
          // Prevent rapid duplicate increments until fetch completes
          pagingGateRef.current = true;
          setPage((prev) => prev + 1);
        }
      },
      { root: rootEl, rootMargin: "200px", threshold: 0 }
    );

    observerRef.current.observe(loaderEl);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // ✅ Handle user selection toggle
  const handleUserSelect = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleClear = () => setSelectedUsers([]);

const handleAdd = () => {
  if (selectedUsers.length === 0) {
    toast.error("Please add members");
    return;
  }

  navigate("/create-vibe?step=2", {
    state: {
      ...location.state,
      members: selectedUsers,
      formData: {
        ...formData,
        members: selectedUsers,
      }
    },
  });
};


  // Navigate to main page when user clicks top header back button
  const handleBackToMain = () => {
    navigate("/home");
  };

  const handleStepBack = () => {
    // if (onBack) {
    //   
    //   onBack();
    // } else {
    if (location && location?.state && location?.state?.formData) {
      navigate("/create-vibe?step=2", {
        state: location?.state,
      });
    } else {
      navigate("/create-vibe?step=2");
    }
    // }
  };


  // Server returns already-filtered users
  const filteredUsers = users;

  return (
    <CustomPageHeader
      hideBack={false}
      showPageHeader={true}
      // pageContainerClass="px-2"
      title={t("create_your_vibe", "Create Your Vibe")}
      onBack={handleBackToMain}
    >
      <div
        className={`flex flex-col gap-4 z-50 ${
          isDarkMode ? "bg-black" : "bg-white"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-2">
          <button
            onClick={handleStepBack}
            className="px-2 py-1 text-[#6d2af6] font-semibold"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h2
            className={`text-lg font-bold ${
              isDarkMode ? "text-white" : "text-black"
            }`}
          >
            {t("add_individual", "Add Individual")}
          </h2>
          <button
            onClick={handleClear}
            className="px-2 py-1 text-[#6d2af6] font-semibold"
          >
            {t("clear")}
          </button>
        </div>

        {/* Search Bar */}
        <input
          type="text"
          placeholder={t("search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`w-full mb-2 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6d29f6] ${
            isDarkMode
              ? "border-gray-600 placeholder-gray-400"
              : "border-gray-300 placeholder-gray-500"
          }`}
          style={
            isDarkMode
              ? { backgroundColor: "#252739", color: "#ffffff" }
              : { backgroundColor: "#ffffff", color: "#000000" }
          }
        />

        {/* User List */}
        <div
          ref={scrollContainerRef}
          className="flex flex-col gap-3 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 180px)", paddingBottom: "70px" }}
        >
          {error && <div className="text-red-500 text-center">{error}</div>}

          {filteredUsers.length > 0
            ? filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center justify-between p-4 rounded-lg shadow-md ${
                    isDarkMode ? "bg-[#252739]" : "bg-white"
                  }`}
                >
                <div className="flex items-center gap-3">
                    <img
                      src={user.image || "/vibe_newlogo.png"}
                      alt={user.full_name}
                      className="w-10 h-10 rounded-full bg-gray-200 object-cover"
                    />
                  <div>
                      <div
                        className={`font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {user.full_name}
                      </div>
                      <div
                        className={`text-sm ${
                          isDarkMode ? "text-white" : "text-[#6d2af6]"
                        }`}
                      >
                        {user.user_name || ""}
                      </div>
                  </div>
                </div>

                <label className="relative flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserSelect(user.id)}
                      className="peer sr-only"
                    />

                    <div
                      className={`h-5 w-5 border-2 rounded-md flex items-center justify-center 
                  transition-all duration-200 ${
                    selectedUsers.includes(user.id)
                      ? "bg-[#6d29f6] border-[#6d29f6]"
                      : "bg-transparent border-[#6d29f6]"
                  }`}
                    >
                    {selectedUsers.includes(user.id) && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="white"
                          className="w-3 h-3"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8.5 11.793l6.793-6.793a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                      </svg>
                    )}
                  </div>
                </label>
              </div>
            ))
            : !loading && (
                <div
                  className={`text-center ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {t("no_data_available")}
                </div>
          )}

          {/* Loader */}
          {loading && (
            <div className="flex justify-center py-4">
              <Loader loaderColor="#6d2af6" />
            </div>
          )}

          {/* End Message */}
          {/* {!hasMore && !loading && users.length > 0 && (
          <div className="text-center text-gray-400 text-sm">
            No more users
          </div>
        )} */}

          {/* Sentinel for infinite scroll */}
          <div ref={loaderRef} className="h-1" />
        </div>

        <div className="fixed left-0 right-0 bottom-0 z-50 block px-4 py-4 md:hidden">
  <button
            className="w-full py-3 font-semibold text-white rounded-xl"
    style={{ background: "#6d2af6" }}
    onClick={handleAdd}
  >
    {t("add")}
  </button>
</div>
        <div className="hidden md:block mt-4">
          <button
            className="w-full py-3 font-semibold text-white"
            style={{ background: "#6d2af6", borderRadius: 0 }}
            onClick={handleAdd}
          >
            {t("add")}
          </button>
        </div>
      </div>
    </CustomPageHeader>
  );
};

export default AddIndividual;
